import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defaultQueryOptions } from '@/config/react-query.config';
import { toast } from 'react-hot-toast';
import { bookingTypeService } from '@/services/bookingType.service';
import { useMemberEntitlements } from './useMemberEntitlements';
import {
  GetAllRequestDto,
  CreateBookingTypeDto,
  UpdateBookingTypeDto,
  facilityTypeTitles,
  FacilityType,
  BookingTypeUI,
} from '@/types/booking.types';

// Query keys
export const bookingTypeKeys = {
  all: ['booking-types'] as const,
  lists: () => [...bookingTypeKeys.all, 'list'] as const,
  list: (params: GetAllRequestDto) =>
    [...bookingTypeKeys.lists(), params] as const,
  details: () => [...bookingTypeKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingTypeKeys.details(), id] as const,
  plan: (planId: string) => [...bookingTypeKeys.all, 'plan', planId] as const,
  active: (locationId?: string) =>
    [...bookingTypeKeys.all, 'active', locationId] as const,
  byFacility: (facilityType: string, locationId?: string) =>
    [...bookingTypeKeys.all, 'facility', facilityType, locationId] as const,
};

// Hook to get all booking types with pagination
export const useBookingTypes = (params?: GetAllRequestDto) => {
  return useQuery({
    queryKey: bookingTypeKeys.list(params || {}),
    queryFn: () => bookingTypeService.getBookingTypes(params),
    ...defaultQueryOptions,
  });
};

// Hook to get booking type by ID
export const useBookingType = (id: string) => {
  return useQuery({
    queryKey: bookingTypeKeys.detail(id),
    queryFn: () => bookingTypeService.getBookingTypeById(id),
    enabled: !!id,
    ...defaultQueryOptions,
  });
};

// Hook to get booking types for a plan
export const usePlanBookingTypes = (planId: string) => {
  return useQuery({
    queryKey: bookingTypeKeys.plan(planId),
    queryFn: () => bookingTypeService.getPlanBookingTypes(planId),
    enabled: !!planId,
    ...defaultQueryOptions,
  });
};

// Hook to get active booking types for UI
export const useActiveBookingTypes = (locationId?: string) => {
  return useQuery({
    queryKey: bookingTypeKeys.active(locationId),
    queryFn: () => {
      if (locationId) {
        return bookingTypeService.getBookingTypesByLocation(locationId);
      }
      return bookingTypeService.getActiveBookingTypes(locationId);
    },
    enabled: !!locationId,
    ...defaultQueryOptions,
  });
};

// Hook to get booking types by facility type
export const useBookingTypesByFacilityType = (
  facilityType: string,
  locationId?: string
) => {
  return useQuery({
    queryKey: bookingTypeKeys.byFacility(facilityType, locationId),
    queryFn: () =>
      bookingTypeService.getBookingTypesByFacilityType(
        facilityType,
        locationId
      ),
    enabled: !!facilityType,
    ...defaultQueryOptions,
  });
};

// Hook to get booking types formatted for UI (BookingType page)
export const useUIBookingTypes = (locationId?: string) => {
  const { data: entitlements } = useMemberEntitlements();

  const query = useQuery<BookingTypeUI[], Error, BookingTypeUI[], readonly unknown[]>({
    queryKey: bookingTypeKeys.active(locationId),
    queryFn: async (): Promise<BookingTypeUI[]> => {
      // Only fetch booking types when locationId is provided
      if (!locationId) {
        return [];
      }

      const bookingTypes = await bookingTypeService.getBookingTypesByLocation(locationId);

      // Transform to UI format
      return bookingTypes.map(
        (type): BookingTypeUI => ({
          id: type.id,
          title: facilityTypeTitles[type.type] || type.type,
          description: `$${type.price}`, // Show $ symbol instead of currency code
          facilityType: type.type,
          price: type.price,
          currency: type.currency,
          minPrice: type.minPrice,
          maxPrice: type.maxPrice,
          active: type.active,
          icon: getFacilityIcon(type.type),
          fields: type.fields,
          viaEntitlement: type.viaEntitlement,
        })
      );
    },
    retryOnMount: false,
    ...defaultQueryOptions,
  });

  // This hook feeds BOTH UI trees — v1 `pages/Booking/BookingType.tsx` and v2
  // `v2/screens/BookingType.tsx`, mounted on the same route by App.tsx's
  // `UiRoute`. `remainingCount` is deliberately the pre-booking balance; the
  // success screens decrement it optimistically rather than refetching this
  // hook (D-09). Only entries already flagged `viaEntitlement` get a
  // `remainingCount` — a plan-granted tile never shows a balance pill even
  // when the member happens to hold a ledger balance for the same facility.
  const remainingByFacility = useMemo(
    () => new Map((entitlements?.data ?? []).map((e) => [e.facilityType, e.remainingCount])),
    [entitlements]
  );

  const data = useMemo(
    () =>
      (query.data ?? []).map((t) =>
        t.viaEntitlement ? { ...t, remainingCount: remainingByFacility.get(t.facilityType) } : t
      ),
    [query.data, remainingByFacility]
  );

  return { ...query, data };
};

// Mutation to create booking type
export const useCreateBookingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBookingTypeDto) =>
      bookingTypeService.createBookingType(data),
    onSuccess: () => {
      toast.success('Booking type created successfully');
      queryClient.invalidateQueries({ queryKey: bookingTypeKeys.lists() });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to create booking type'
      );
    },
  });
};

// Mutation to update booking type
export const useUpdateBookingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingTypeDto }) =>
      bookingTypeService.updateBookingType(id, data),
    onSuccess: (_, { id }) => {
      toast.success('Booking type updated successfully');
      queryClient.invalidateQueries({ queryKey: bookingTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingTypeKeys.detail(id) });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to update booking type'
      );
    },
  });
};

// Mutation to delete booking type
export const useDeleteBookingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => bookingTypeService.deleteBookingType(id),
    onSuccess: () => {
      toast.success('Booking type deleted successfully');
      queryClient.invalidateQueries({ queryKey: bookingTypeKeys.lists() });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to delete booking type'
      );
    },
  });
};

// Helper function to get facility icon
function getFacilityIcon(facilityType: FacilityType): string {
  const iconMap: Record<FacilityType, string> = {
    [FacilityType.HITTING_BAY]: 'golf',
    [FacilityType.GYM]: 'fitness',
    [FacilityType.CLUB_FITTING]: 'tools',
    [FacilityType.RECOVERY_ROOM]: 'heart',
    [FacilityType.CONFERENCE_ROOM]: 'users',
    [FacilityType.BOX_ASSESSMENT]: 'clipboard',
    [FacilityType.PRIVATE_HITTING_BAY]: 'lock',
    [FacilityType.PUTT_VIEW]: 'target',
    [FacilityType.WORKSPACE]: 'briefcase',
    [FacilityType.COACHING_LESSON]: 'golf',
    [FacilityType.COACHING_LESSON_30]: 'golf',
    [FacilityType.FITNESS_LESSON]: 'fitness',
    [FacilityType.SAUNA]: 'flame',
    [FacilityType.SHOWER]: 'droplet',
    [FacilityType.MASSAGE_CHAIR]: 'armchair',
  };

  return iconMap[facilityType] || 'default';
}
